import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const statusFlow = ['raised', 'approved', 'in-progress', 'implemented', 'effectiveness-check', 'closed'];

const statusConfig = {
  raised: { label: 'Raised', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  approved: { label: 'Approved', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  'in-progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  implemented: { label: 'Implemented', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  'effectiveness-check': { label: 'Effectiveness Check', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  closed: { label: 'Closed', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
};

const RecommendationClosurePanel = ({ recommendations, onUpdateRecommendation }) => {
  const [selectedRec, setSelectedRec] = useState(null);
  const [closureForm, setClosureForm] = useState({ effectivenessResult: '', notes: '', newStatus: '' });
  const [attachments, setAttachments] = useState([]);

  const handleSelectRec = (rec) => {
    setSelectedRec(rec);
    setClosureForm({ effectivenessResult: rec?.effectivenessResult || '', notes: '', newStatus: rec?.status || 'raised' });
    setAttachments([]);
  };

  const handleStatusUpdate = () => {
    if (!closureForm?.newStatus) return;
    onUpdateRecommendation?.({
      ...selectedRec,
      status: closureForm?.newStatus,
      effectivenessResult: closureForm?.effectivenessResult,
      closedAt: closureForm?.newStatus === 'closed' ? new Date()?.toISOString() : selectedRec?.closedAt,
    });
    setSelectedRec(prev => ({ ...prev, status: closureForm?.newStatus }));
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files || []);
    setAttachments(prev => [...prev, ...files?.map(f => ({ name: f?.name, size: f?.size }))]);
  };

  const getNextStatuses = (current) => {
    const idx = statusFlow?.indexOf(current);
    if (idx === -1 || idx >= statusFlow?.length - 1) return [];
    return statusFlow?.slice(idx + 1, idx + 3);
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
            <Icon name="CheckCircle2" size={18} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Recommendation Closure</h3>
        </div>

        <div className="space-y-3">
          {recommendations?.map(rec => {
            const status = statusConfig?.[rec?.status] || statusConfig?.raised;
            const isSelected = selectedRec?.id === rec?.id;
            return (
              <div
                key={rec?.id}
                className={`border rounded-xl overflow-hidden transition-all ${isSelected ? 'border-primary ring-1 ring-primary/20' : 'border-border'}`}
              >
                <button
                  onClick={() => handleSelectRec(isSelected ? null : rec)}
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${status?.color}`}>{status?.label}</span>
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs capitalize">{rec?.type}</span>
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs capitalize">{rec?.priority} priority</span>
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">{rec?.description}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      {rec?.dueDate && <span className="flex items-center gap-1"><Icon name="Calendar" size={11} />Due: {format(new Date(rec.dueDate), 'MMM dd, yyyy')}</span>}
                      {rec?.closedAt && <span className="flex items-center gap-1 text-emerald-600"><Icon name="CheckCircle" size={11} />Closed: {format(new Date(rec.closedAt), 'MMM dd, yyyy')}</span>}
                    </div>
                  </div>
                  <Icon name={isSelected ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                </button>

                {isSelected && (
                  <div className="px-4 pb-4 border-t border-border bg-muted/20 space-y-4">
                    {/* Status lifecycle */}
                    <div className="pt-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Status Lifecycle</p>
                      <div className="flex items-center gap-1 flex-wrap">
                        {statusFlow?.map((s, i) => {
                          const sIdx = statusFlow?.indexOf(rec?.status);
                          const isPast = i < sIdx;
                          const isCurrent = i === sIdx;
                          return (
                            <React.Fragment key={s}>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                isCurrent ? 'bg-primary text-primary-foreground' :
                                isPast ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400': 'bg-muted text-muted-foreground'
                              }`}>
                                {isPast && <Icon name="Check" size={10} className="inline mr-0.5" />}
                                {statusConfig?.[s]?.label}
                              </span>
                              {i < statusFlow?.length - 1 && <Icon name="ChevronRight" size={12} className="text-muted-foreground" />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>

                    {/* Advance status */}
                    {getNextStatuses(rec?.status)?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Advance Status</p>
                        <div className="flex gap-2 flex-wrap">
                          {getNextStatuses(rec?.status)?.map(s => (
                            <button
                              key={s}
                              onClick={() => setClosureForm(p => ({ ...p, newStatus: s }))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                closureForm?.newStatus === s
                                  ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/40 text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              → {statusConfig?.[s]?.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Effectiveness result (for effectiveness-check or closed) */}
                    {['effectiveness-check', 'closed']?.includes(closureForm?.newStatus || rec?.status) && (
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Effectiveness Check Result</label>
                        <textarea
                          placeholder="Document the effectiveness check outcome..."
                          value={closureForm?.effectivenessResult}
                          onChange={e => setClosureForm(p => ({ ...p, effectivenessResult: e?.target?.value }))}
                          rows={2}
                          className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
                        />
                      </div>
                    )}

                    {/* Evidence upload for closure */}
                    {closureForm?.newStatus === 'closed' && (
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Closure Evidence (required for closure)</label>
                        <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-dashed border-border rounded-lg hover:border-primary/40 hover:bg-muted/30 transition-all">
                          <Icon name="Upload" size={14} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Upload evidence files</span>
                          <input type="file" multiple className="hidden" onChange={handleFileInput} />
                        </label>
                        {attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {attachments?.map((f, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Icon name="File" size={12} />
                                <span>{f?.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {closureForm?.newStatus && closureForm?.newStatus !== rec?.status && (
                      <button
                        onClick={handleStatusUpdate}
                        className="px-4 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Update to "{statusConfig?.[closureForm?.newStatus]?.label}"
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {(!recommendations || recommendations?.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="CheckCircle2" size={28} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No recommendations to close</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationClosurePanel;
